import axios from 'axios'
import React, { useContext, useEffect, useMemo, useState, useRef } from 'react'
import { useTable, useSortBy, usePagination } from 'react-table'
import { useHistory } from 'react-router-dom'
import { StateContext, DispatchContext } from '../../AppContext'
import Button from 'src/components//button/Button'
import Header from 'src/components//header/Header'
import { baseApiURL } from 'src/utils/utils'
import SearchField from 'src/components/searchField/SearchField'
import styles from './companies.module.scss'
import { IState } from 'src/Interface.js'
import { toast } from 'react-toastify'
import Footer from 'src/components/footer/Footer'
import Table from 'src/components/table/Table'
import SubHeader from 'src/components/subHeader/SubHeader'
import useCancellablePromise from 'src/utils/useCancellablePromise'
import useDebounce from 'src/hooks/useDebounce'

const Companies: React.FC = () => {
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)
  const [loading, setLoading] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const { cancellablePromise } = useCancellablePromise()
  const history = useHistory()
  const [hideArchieved, toggleShowArchieved] = useState(window.localStorage.getItem('hideCompanyArchieved') === 'true' ? true : false || false)
  const [totalCount, setTotalCount] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [companies, setCompanies] = useState([])
  const debouncedSearchValue = useDebounce(searchValue, 300)
  const pageSize = 10
  const prevHideArchievedRef = useRef(hideArchieved)
  const prevSearchRef = useRef(debouncedSearchValue)
  const isInitialMount = useRef(true)
  const state: IState = useContext(StateContext)

  const toggleArchieved = (hideArchieved) => {
    window.localStorage.setItem('hideCompanyArchieved', hideArchieved)
    toggleShowArchieved(hideArchieved)
  }

  const data = useMemo(() => companies, [companies])
  const columns = useMemo(() => [
    {
      Header: 'Firmanavn',
      accessor: 'name',
      Cell: properties => {
        if (properties.row.original.avatarImageUrl) {
          return (
            <span className='tw-flex tw-items-center'>
              <img className={styles.logo} src={properties.row.original.avatarImageUrl} alt="" />
              {properties.value}
            </span>
          )
        } else {
          return (
            <span className='tw-flex tw-items-center'>
              <span style={{ background: properties.row.original.hexColor }} className={styles.icon}>{properties.value.charAt(0)}</span>
              {properties.value}
            </span>
          )
        }
      }
    },
    {
      Header: 'Kontaktperson',
      accessor: 'contactPerson'
    },
    {
      Header: 'Medarbejdere',
      accessor: 'employees.totalUsersCount'
    },
    {
      Header: 'Ture kørt',
      accessor: 'ordersCount'
    },
    {
      Header: 'Id',
      accessor: 'id'
    },
    {
      Header: 'Aktiv',
      accessor: 'status',
      Cell: (cell) => cell.value === 'ACTIVE' ? <span className='status status--green'>Aktiv</span> : <span className='status status--red'>Inaktiv</span>
    }
  ], [])


  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    canNextPage,
    canPreviousPage,
    pageOptions,
    previousPage,
    gotoPage,
    state: pageState,
    nextPage,
    prepareRow,
  } = useTable({
    columns,
    data,
    autoResetPage: false,
    autoResetSortBy: false,
    manualPagination: true,
    pageCount: totalCount > 0 ? Math.ceil(totalCount / pageSize) : 0
  }, useSortBy, usePagination)

  // Reset to first page when hideArchieved or search changes
  useEffect(() => {
    const hideArchievedChanged = prevHideArchievedRef.current !== hideArchieved
    const searchChanged = prevSearchRef.current !== debouncedSearchValue
    
    if ((hideArchievedChanged || searchChanged) && pageState.pageIndex > 0) {
      gotoPage(0)
    }
    
    prevHideArchievedRef.current = hideArchieved
    prevSearchRef.current = debouncedSearchValue
  }, [hideArchieved, debouncedSearchValue, gotoPage, pageState.pageIndex])

  useEffect(() => {
    if (state.user.accessToken) {
      // Skip API call if search value is 1-2 characters (wait for at least 3 characters)
      if (debouncedSearchValue && debouncedSearchValue.length > 0 && debouncedSearchValue.length < 3) {
        return
      }

      // Use tableLoading for subsequent loads, loading only for initial mount
      if (isInitialMount.current) {
        setLoading(true)
        isInitialMount.current = false
      } else {
        setTableLoading(true)
      }
      
      // Convert 0-based pageIndex to 1-based page number for API
      const currentPage = (pageState.pageIndex || 0) + 1
      const currentSort = pageState.sortBy?.[0]
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString()
      })
      if (currentSort?.id) {
        queryParams.append('sortBy', currentSort.id)
        queryParams.append('sortOrder', currentSort.desc ? 'DESC' : 'ASC')
      }
      
      if (hideArchieved) {
        queryParams.append('status', 'ACTIVE')
      }

      if (debouncedSearchValue && debouncedSearchValue.length >= 3) {
        queryParams.append('searchText', debouncedSearchValue)
      }

      cancellablePromise(axios(`${baseApiURL()}/companies?${queryParams.toString()}`, { 
        headers: { Authorization: `Bearer ${state.user.accessToken}` } 
      })).then((result) => {
        const responseData = result.data.records || result.data
        const fetchedCompanies = Array.isArray(responseData) ? responseData : []
        const totalRecords = result.data.totalRecords || fetchedCompanies.length
        
        setLoading(false)
        setTableLoading(false)
        setTotalCount(totalRecords)
        setCompanies(fetchedCompanies)
        // Also update global state for other components that might need it
        dispatch({ type: 'SET_COMPANIES', companies: fetchedCompanies })
      }).catch((err) => {
        setLoading(false)
        setTableLoading(false)
        toast(err.response?.data?.message || 'Fejl ved indlæsning af kunder')
      })
    }
  }, [state.user.accessToken, dispatch, hideArchieved, cancellablePromise, pageState.pageIndex, pageState.sortBy, debouncedSearchValue])

  // Redirect to order page if user role is loaded and not DanTaxiAdmin
  useEffect(() => {
    if (state.user.role && state.user.role !== 'DanTaxiAdmin') {
      history.push('/order')
    }
  }, [state.user.role, history])

  return (
    <>
      <Header />
      <SubHeader title='Kunder' loading={false}>
        <Button type='secondary' label='Indstillinger' iconLeft={<svg xmlns="http://www.w3.org/2000/svg" className="tw-h-6 tw-w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>} modifiers='tw-mr-5' handleClick={() => dispatch({ type: 'SHOW_ASIDE', show: true, checked: hideArchieved, asideType: 'Settings', size: 'small', ToggleSwitchLabel: 'Skjul deaktiverede medarbejdere', handleClick: toggleArchieved })} />
        <SearchField name='search' label='Søg' placeholder='Søg' handleInputChange={(value) => setSearchValue(value)} />
        <Button modifiers='tw-ml-5' type='primary' label='Opret kunde' handleClick={() => dispatch({ showModal: true, type: 'SHOW_MODAL', modalType: 'addCompany', hideArchieved })} />
      </SubHeader>
      <main className='container'>
        <Table
          getTableBodyProps={getTableBodyProps}
          getTableProps={getTableProps}
          prepareRow={prepareRow}
          page={page}
          loading={tableLoading}
          handleClick={(companyId) => history.push(`/companies/${companyId}`)}
          headerGroups={headerGroups}
          type='companies'
        />
      </main>
      <Footer
        pageState={pageState}
        canNextPage={canNextPage}
        nextPage={nextPage}
        canPreviousPage={canPreviousPage}
        page={page}
        loading={false}
        paginationLabel='kunder'
        gotoPage={gotoPage}
        previousPage={previousPage}
        pageOptions={pageOptions}
        itemsLength={totalCount}
      />
    </>
  )
}

export default Companies
